import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useEntornoFormacion } from './entorno'
import { ICONOS, Trazo, mmss } from './comun'
import { t as i18nT, tx, marca, tDato } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL REPRODUCTOR DE LA FORMACIÓN (uno solo: la página de Formación y el centro de ayuda)   (t222 · 14-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  EL VÍDEO NO TIENE DIRECCIÓN PÚBLICA. Al pulsar el play se le pide al servidor un enlace FIRMADO de pocos minutos
//  (GET /api/formacion/videos/:num/enlace, solo con sesión y por la puerta de la formación). Si el enlace caduca con el
//  vídeo en pausa, al seguir se pide otro solo y se continúa donde iba. La miniatura y los subtítulos llegan también por
//  la API con sesión y se le dan al vídeo desde la memoria del navegador: los subtítulos llevan la narración entera.
//
//  CONTROLES PROPIOS (rediseño aprobado por el dueño, 14-oct): la barra con las marcas de cada capítulo, el tiempo y el
//  capítulo en el que va, la velocidad (1×, 1,25×, 1,5×, 2×), los subtítulos (que pinta él, encima del vídeo) y la pantalla
//  completa — la de la CAJA, para que la marca no se quede fuera, y simulada en el iPhone, que no deja poner una caja a
//  pantalla completa. Con el teclado: espacio, ← →, F y C. Al acabar, una celebración discreta y la siguiente lección.
//
//  LO QUE FRENA LA COPIA, dicho como es (plan, §4): sin botón de descargar, sin menú del botón derecho y sin imagen
//  dentro de imagen. Encima, quieta y discreta, la marca «Formación Biango» y NADA MÁS: el dueño (14-oct), «arriba no
//  puede salir mi nombre ni mi correo […] debe salir "Formación Biango" nada más». Ni nombre, ni correo, ni clínica, ni hora.
//
//  CUANDO NO SE PUEDE VER: dentro de la caja, un aviso en llano («la explicación está aquí debajo») con «Volver a intentar»,
//  nunca una caja negra. Si el servidor ya dice que los vídeos no están (`disponible === false`), el cartel sale antes de
//  pulsar nada. Al equipo de Biango el servidor le manda además la causa exacta (`detalle`) y sale debajo del aviso; a la
//  clínica no le llega. Si el vídeo no arranca, se pide otro enlace (y se prueba la `alternativa` con «.eu», si la trae) y,
//  si sigue sin arrancar, se le pregunta al servidor con ?comprobar=1.
//
//  LA VISTA AUTOMÁTICA: al llegar al 90 % o al final se manda `terminado` y la lección queda vista (onVista). Mientras,
//  cada 15 segundos se apunta hasta dónde va, para seguir otro día donde lo dejó.
const MARCA = marca('components.formacion.ReproductorFormacion.formacion_biango', 'Formación Biango')
// Los colores de la Formación (los pone .fm, en comun.jsx). El reproductor sale también en el chat de ayuda, fuera de .fm:
// ahí tira del color de la clínica, igual que dentro, y nunca de un color fijo.
const MARCA_CLINICA = 'var(--marca-clinica, var(--biango-principal, #1B2130))'
const C_MARCA = 'var(--fm-marca, ' + MARCA_CLINICA + ')'
const C_BOTON = 'var(--fm-boton, ' + MARCA_CLINICA + ')'
const C_SOBRE_BOTON = 'var(--fm-sobre-boton, var(--marca-texto, #FFFFFF))'
const C_ACENTO = 'var(--fm-acento, color-mix(in srgb, ' + MARCA_CLINICA + ' 38%, #FFFFFF))'
const VELOCIDADES = [1, 1.25, 1.5, 2]
const velTexto = (v) => String(v).replace('.', ',') + '×'

// Lo que dice la caja cuando el servidor no da el vídeo.
const esSuspendida = (e) => !!(e && e.response && e.response.status === 403 && e.response.data
  && e.response.data.codigo === 'FORMACION_CERRADA' && e.response.data.motivo === 'suspendida')
function falloDe(e) {
  const d = (e && e.response && e.response.data) || {}
  if (e && e.response && e.response.status === 429) return { titulo: i18nT('components.formacion.ReproductorFormacion.has_abierto_muchos_videos_seguidos', 'Has abierto muchos vídeos seguidos'), texto: d.error || i18nT('components.formacion.ReproductorFormacion.vuelve_a_intentarlo_dentro_de', 'Vuelve a intentarlo dentro de un rato.'), detalle: d.detalle || null }
  // t287 · la Formación cortada a esta persona (el punto 6 de las condiciones): el aviso del servidor, tal cual.
  if (esSuspendida(e)) return { titulo: i18nT('components.formacion.ReproductorFormacion.tu_acceso_a_la_formacion', 'Tu acceso a la Formación está suspendido'), texto: d.error || i18nT('components.formacion.ReproductorFormacion.para_cualquier_duda_escribe_a', 'Para cualquier duda, escribe a info@biango.es.'), detalle: null, suspendida: true }
  if (e && e.response) return { titulo: i18nT('components.formacion.ReproductorFormacion.este_video_no_se_puede', 'Este vídeo no se puede ver ahora'), texto: d.error || i18nT('components.formacion.ReproductorFormacion.tienes_la_explicacion_paso_a', 'Tienes la explicación paso a paso aquí debajo.'), detalle: d.detalle || null }
  return { titulo: i18nT('components.formacion.ReproductorFormacion.no_se_ha_podido_abrir', 'No se ha podido abrir el vídeo'), texto: i18nT('components.formacion.ReproductorFormacion.comprueba_la_conexion_y_vuelve', 'Comprueba la conexión y vuelve a intentarlo. La explicación paso a paso está aquí debajo.'), detalle: null }
}

// Los subtítulos (.vtt) en trozos con su principio y su final, para pintarlos encima del vídeo.
function leerVtt(texto) {
  const seg = (t) => { const p = t.split(':'); return Number(p[0]) * 3600 + Number(p[1]) * 60 + parseFloat(p[2]) }
  const out = []
  for (const b of String(texto || '').replace(/\r/g, '').split(/\n\n+/)) {
    const m = /(\d\d:\d\d:\d\d\.\d+)\s+-->\s+(\d\d:\d\d:\d\d\.\d+)[^\n]*\n([\s\S]+)/.exec(b)
    if (m) out.push({ desde: seg(m[1]), hasta: seg(m[2]), texto: m[3].replace(/\s*\n\s*/g, ' ').trim() })
  }
  return out
}

const ReproductorFormacion = forwardRef(function ReproductorFormacion({ num, duracionSeg = 0, posicionInicial = 0, onVista, compacto = false,
  capitulos = [], disponible = true, onTiempo, celebracion = null, plano = false }, refExterna) {
  // B-2283 · el cliente de la Formación (el del equipo, o el de una persona invitada) y, solo en la de la persona invitada, su
  // `sello` (components/formacion/entorno): lo que se pinta abajo a la izquierda, también en pantalla completa.
  const { api, sello } = useEntornoFormacion()
  const cajaRef = useRef(null)
  const videoRef = useRef(null)
  const barraRef = useRef(null)
  const [cartel, setCartel] = useState(null)
  const [cues, setCues] = useState([])
  const [src, setSrc] = useState(null)
  const [estado, setEstado] = useState('parado')      // parado | cargando | listo | error
  const [fallo, setFallo] = useState(null)            // { titulo, texto, detalle }
  const [t, setT] = useState(posicionInicial > 0 ? posicionInicial : 0)
  const [dur, setDur] = useState(duracionSeg || 0)
  const [sonando, setSonando] = useState(false)
  const [vel, setVel] = useState(0)
  const [cc, setCc] = useState(true)
  const [completa, setCompleta] = useState(false)      // pantalla completa simulada (iPhone)
  const [enPantallaCompleta, setEnPantallaCompleta] = useState(false)
  const [celebrar, setCelebrar] = useState(false)
  const [verControles, setVerControles] = useState(true)
  const ocultar = useRef(null)
  const seguir = useRef({ desde: 0, renovaciones: 0, ultimoEnvio: 0, visto: 0, ultimoT: null, terminado: false, alternativa: null, conAlternativa: false, detalle: null, pendiente: null })

  // La miniatura y los subtítulos, por la API (con la sesión). Se sueltan al desmontar.
  useEffect(() => {
    let vivo = true
    const urls = []
    api.get(`/formacion/videos/${num}/miniatura`, { responseType: 'blob' })
      .then(r => { if (!vivo) return; const u = URL.createObjectURL(r.data); urls.push(u); setCartel(u) }).catch(() => {})
    api.get(`/formacion/videos/${num}/subtitulos`, { responseType: 'text' })
      .then(r => { if (vivo) setCues(leerVtt(r.data)) }).catch(() => {})
    return () => { vivo = false; urls.forEach(u => URL.revokeObjectURL(u)) }
  }, [num])

  // La pantalla completa de verdad (la de la caja): el botón cambia de icono al entrar y al salir (también con Esc).
  useEffect(() => {
    const h = () => setEnPantallaCompleta(!!document.fullscreenElement && document.fullscreenElement === cajaRef.current)
    document.addEventListener('fullscreenchange', h)
    return () => document.removeEventListener('fullscreenchange', h)
  }, [])
  useEffect(() => {
    if (!completa) return undefined
    const h = (e) => { if (e.key === 'Escape') setCompleta(false) }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [completa])

  // Pide un enlace. Se queda con su `alternativa` (la dirección con «.eu») y con el `detalle` para el equipo de Biango.
  const pedirEnlace = async (comprobar = false) => {
    const r = await api.get(`/formacion/videos/${num}/enlace`, comprobar ? { params: { comprobar: 1 } } : undefined)
    const d = r.data || {}
    seguir.current.alternativa = d.alternativa || null
    seguir.current.detalle = d.detalle || null
    return seguir.current.conAlternativa && d.alternativa ? d.alternativa : d.url
  }

  const empezar = async (desde) => {
    setFallo(null)
    setCelebrar(false)
    setEstado('cargando')
    seguir.current.renovaciones = 0
    try {
      const url = await pedirEnlace()
      if (!url) throw new Error(i18nT('components.formacion.ReproductorFormacion.sin_enlace', 'sin enlace'))
      const inicio = desde != null ? desde : (posicionInicial > 5 && (!duracionSeg || posicionInicial < duracionSeg * 0.9) ? posicionInicial : 0)
      seguir.current.desde = inicio
      setSrc(url)
      setEstado('listo')
    } catch (e) {
      setSrc(null)
      setEstado('error')
      setFallo(falloDe(e))
    }
  }

  // Lo que se le pide desde fuera (los capítulos y «lo que dice el vídeo» de la lección): ir a un segundo y que suene.
  useImperativeHandle(refExterna, () => ({
    ir: (seg) => {
      const v = videoRef.current
      setCelebrar(false)
      if (v && src) { try { v.currentTime = seg; v.play().catch(() => {}) } catch (_) { /* aún sin metadatos */ } setT(seg); return }
      empezar(seg)
    },
  }))

  // El vídeo no arranca o se corta: primero la `alternativa` (si la hay), luego otro enlace (el anterior pudo caducar) y,
  // al final, se le pregunta al servidor por qué (?comprobar=1), para que el equipo de Biango vea la causa.
  const alFallar = async () => {
    const v = videoRef.current
    if (!src || !v) return
    const s = seguir.current
    s.desde = v.currentTime || s.desde
    if (s.alternativa && !s.conAlternativa) { s.conAlternativa = true; setSrc(s.alternativa); return }
    if (s.renovaciones < 1) {
      s.renovaciones += 1
      try { const url = await pedirEnlace(); if (url) { setSrc(url); return } } catch (_) { /* se dice abajo */ }
    }
    let detalle = s.detalle
    try { await pedirEnlace(true); detalle = s.detalle || detalle } catch (e) { const f = falloDe(e); detalle = f.detalle || detalle }
    const codigo = v.error && v.error.code
    setSrc(null)
    setEstado('error')
    setFallo({ titulo: i18nT('components.formacion.ReproductorFormacion.este_video_no_se_puede', 'Este vídeo no se puede ver ahora'), texto: i18nT('components.formacion.ReproductorFormacion.tienes_la_explicacion_paso_a_2', 'Tienes la explicación paso a paso aquí debajo. Vuelve a intentarlo en un rato.'), detalle: detalle ? detalle + (codigo ? i18nT('components.formacion.ReproductorFormacion.el_navegador_error', ' (el navegador: error {{codigo}})', { codigo }) : '') : null })
  }

  const enviar = (terminado = false) => {
    const v = videoRef.current
    if (!v) return
    const s = seguir.current
    s.ultimoEnvio = Date.now()
    api.post(`/formacion/lecciones/${num}/progreso`, { posicion_seg: Math.round(v.currentTime || 0), visto_seg: Math.round(s.visto), terminado })
      .then(r => { if (terminado && r.data && r.data.vista_el && onVista) onVista(r.data.vista_el) })
      .catch((e) => {
        // t287 · si Biango le corta la Formación mientras mira, el siguiente progreso (cada 15 s) se lleva el 403: el vídeo
        // se para y la caja lo dice. El enlace firmado que ya tenía no se puede retirar, pero deja de verse aquí.
        if (!esSuspendida(e)) return
        try { v.pause() } catch (_) { /* ya parado */ }
        setSrc(null)
        setEstado('error')
        setFallo(falloDe(e))
      })
  }

  const alAvanzar = () => {
    const v = videoRef.current
    if (!v) return
    const s = seguir.current
    const ahora = v.currentTime || 0
    if (s.ultimoT != null && ahora > s.ultimoT && ahora - s.ultimoT < 2) s.visto += ahora - s.ultimoT
    s.ultimoT = ahora
    s.renovaciones = 0
    setT(ahora)
    if (onTiempo) onTiempo(ahora)
    const d = v.duration || duracionSeg || 0
    if (!s.terminado && d > 0 && ahora >= d * 0.9) { s.terminado = true; enviar(true); return }
    if (Date.now() - s.ultimoEnvio > 15000) enviar(false)
  }

  const alAcabar = () => {
    if (!seguir.current.terminado) { seguir.current.terminado = true; enviar(true) }
    setSonando(false)
    if (celebracion && !compacto) setCelebrar(true)
  }

  const alternar = () => {
    const v = videoRef.current
    if (!src || !v) { empezar(); return }
    if (v.paused) { setCelebrar(false); if (v.ended) v.currentTime = 0; v.play().catch(() => {}) } else v.pause()
  }

  const pantallaCompleta = () => {
    const caja = cajaRef.current
    if (!caja) return
    if (completa) { setCompleta(false); return }
    if (document.fullscreenElement) { document.exitFullscreen().catch(() => {}); return }
    if (caja.requestFullscreen) caja.requestFullscreen().catch(() => setCompleta(true))
    else setCompleta(true)   // iPhone: no deja poner en pantalla completa una caja; se simula
  }

  const cambiarVelocidad = () => {
    const n = (vel + 1) % VELOCIDADES.length
    setVel(n)
    if (videoRef.current) videoRef.current.playbackRate = VELOCIDADES[n]
  }

  // Ir a un punto de la barra (con el ratón o con el dedo).
  const irA = (clientX) => {
    const b = barraRef.current
    const v = videoRef.current
    if (!b) return
    const r = b.getBoundingClientRect()
    const x = Math.max(0, Math.min(1, (clientX - r.left) / (r.width || 1)))
    const total = (v && v.duration) || dur || duracionSeg || 0
    const seg = x * total
    if (v && src) { try { v.currentTime = seg } catch (_) { /* aún sin metadatos */ } setT(seg) } else empezar(seg)
  }
  const arrastrar = (e) => {
    irA(e.clientX)
    const mover = (ev) => irA(ev.clientX)
    const soltar = () => { window.removeEventListener('pointermove', mover); window.removeEventListener('pointerup', soltar) }
    window.addEventListener('pointermove', mover)
    window.addEventListener('pointerup', soltar)
  }

  // Los controles se esconden solos mientras suena y vuelven al mover el ratón o tocar.
  const despertar = () => {
    setVerControles(true)
    clearTimeout(ocultar.current)
    ocultar.current = setTimeout(() => { if (videoRef.current && !videoRef.current.paused) setVerControles(false) }, 2600)
  }
  useEffect(() => () => clearTimeout(ocultar.current), [])

  const teclado = (e) => {
    if (e.target && /input|textarea|select/i.test(e.target.tagName)) return
    // Un botón de dentro ya hace lo suyo con el espacio y el Intro: no se repite aquí.
    if (e.target !== cajaRef.current && e.target && /button/i.test(e.target.tagName) && (e.key === ' ' || e.key === 'Enter')) return
    const v = videoRef.current
    if (e.key === ' ' || e.key === 'k') { e.preventDefault(); alternar() }
    else if (e.key === 'ArrowRight' && v && src) { e.preventDefault(); v.currentTime = Math.min((v.duration || 0), v.currentTime + 5) }
    else if (e.key === 'ArrowLeft' && v && src) { e.preventDefault(); v.currentTime = Math.max(0, v.currentTime - 5) }
    else if (e.key === 'f' || e.key === 'F') { e.preventDefault(); pantallaCompleta() }
    else if (e.key === 'c' || e.key === 'C') { e.preventDefault(); setCc(x => !x) }
    despertar()
  }

  const total = dur || duracionSeg || 0
  const pct = total ? Math.min(100, (100 * t) / total) : 0
  let ci = -1
  capitulos.forEach((c, i) => { if (t >= c.seg) ci = i })
  const capituloActual = ci >= 0 ? capitulos[ci].titulo : ''
  const cue = cc ? cues.find(c => t >= c.desde && t < c.hasta) : null
  const sinVideo = disponible === false && estado !== 'error'
  const empezado = !!src
  const mostrarControles = empezado || (!compacto && !plano)
  const visibles = !sonando || verControles
  const radio = completa || enPantallaCompleta || plano ? 0 : (compacto ? 10 : 18)
  // A pantalla completa la caja llega al borde del móvil: los controles y los subtítulos se apartan de la barra del sistema
  // (--sa-abajo, la medida que pone la app o env(); src/index.css), como el resto de lo que va abajo.
  const alBorde = completa || enPantallaCompleta
  const aBorde = (base) => (alBorde ? 'calc(' + base + ' + var(--sa-abajo))' : base)

  return (
    <div data-reproductor-formacion={num}>
      <div ref={cajaRef} onContextMenu={e => e.preventDefault()} onMouseMove={despertar} onTouchStart={despertar} onKeyDown={teclado} tabIndex={0}
        aria-label={i18nT('components.formacion.ReproductorFormacion.video_de_la_leccion_espacio', 'Vídeo de la lección. Espacio para reproducir o pausar, flechas para avanzar o retroceder, F para pantalla completa, C para los subtítulos')}
        // (31-oct-2026, el dueño: «los controles se ocultan debajo del menú inferior del móvil») La simulada mide el alto VISIBLE
        // (--alto-pantalla, 100dvh), no inset:0, que en el móvil llega por detrás de la barra del navegador y de la del sistema;
        // y va en la capa de las ventanas, por encima de los avisos flotantes.
        style={{ position: completa ? 'fixed' : 'relative', top: completa ? 0 : undefined, left: completa ? 0 : undefined, height: completa ? 'var(--alto-pantalla)' : undefined, zIndex: completa ? 'var(--capa-ventana)' : undefined,
          background: '#000', borderRadius: radio, overflow: 'hidden', aspectRatio: completa || enPantallaCompleta ? undefined : '16 / 9', width: '100%',
          boxShadow: compacto || completa || plano ? 'none' : '0 34px 80px rgba(0,0,0,.55)', userSelect: 'none', WebkitUserSelect: 'none', outline: 'none',
          cursor: sonando && !verControles ? 'none' : 'default' }}>
        {src ? (
          <video ref={videoRef} src={src} autoPlay playsInline preload="metadata"
            controlsList="nodownload nofullscreen noremoteplayback" disablePictureInPicture
            poster={cartel || undefined} onClick={alternar}
            onLoadedMetadata={() => { const v = videoRef.current; if (!v) return; setDur(v.duration || duracionSeg); v.playbackRate = VELOCIDADES[vel]; if (seguir.current.desde > 0) { try { v.currentTime = seguir.current.desde } catch (_) {} } }}
            onTimeUpdate={alAvanzar} onEnded={alAcabar}
            onPlay={() => { setSonando(true); despertar() }} onPause={() => { setSonando(false); setVerControles(true); enviar(false) }} onError={alFallar}
            style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', background: '#000' }} />
        ) : estado === 'error' && fallo ? (
          <div role="alert" data-video-no-disponible="" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: compacto ? 6 : 10, padding: compacto ? 14 : 24, textAlign: 'center',
            color: '#fff', background: cartel ? `linear-gradient(rgba(15,23,42,.82), rgba(15,23,42,.88)), center / cover no-repeat url(${cartel})` : 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
            <span style={{ width: compacto ? 38 : 48, height: compacto ? 38 : 48, borderRadius: '50%', background: 'rgba(255,255,255,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width={compacto ? 18 : 22} height={compacto ? 18 : 22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="15" height="14" rx="2" /><path d="m17 10 5-3v10l-5-3" /><path d="M3 3l18 18" /></svg>
            </span>
            <div className="fm-m" style={{ fontSize: compacto ? 13.5 : 17, fontWeight: 800, lineHeight: 1.3 }}>{fallo.titulo}</div>
            <div style={{ fontSize: compacto ? 12 : 13.5, lineHeight: 1.5, maxWidth: 460, color: 'rgba(255,255,255,.86)' }}>{fallo.texto}</div>
            <button type="button" onClick={() => empezar()}
              style={{ marginTop: 2, border: '1px solid rgba(255,255,255,.35)', background: 'rgba(255,255,255,.1)', color: '#fff', borderRadius: 9, padding: compacto ? '6px 12px' : '8px 15px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              {i18nT('components.formacion.ReproductorFormacion.volver_a_intentar', 'Volver a intentar')}
            </button>
            {fallo.detalle && (
              <div data-detalle-para-biango="" style={{ marginTop: 4, maxWidth: 560, fontSize: 11.5, lineHeight: 1.45, color: '#fde68a', background: 'rgba(0,0,0,.35)', borderRadius: 8, padding: '6px 10px', userSelect: 'text', WebkitUserSelect: 'text' }}>
                {tx('components.formacion.ReproductorFormacion.para_el_equipo_de_biango', 'Para el equipo de Biango: {{detalle}}', { detalle: fallo.detalle })}
              </div>
            )}
          </div>
        ) : sinVideo ? (
          <div data-video-llega-en-breve="" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: compacto ? 6 : 10, padding: 24, textAlign: 'center', color: '#fff',
            background: cartel ? `linear-gradient(rgba(10,16,28,.55), rgba(10,16,28,.82)), center / cover no-repeat url(${cartel})` : 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
            <span className="fm-m" style={{ fontSize: compacto ? 14 : 19, fontWeight: 800 }}>{i18nT('components.formacion.ReproductorFormacion.el_video_llega_en_breve', 'El vídeo llega en breve')}</span>
            <span style={{ fontSize: compacto ? 12 : 14, color: 'rgba(255,255,255,.86)', maxWidth: 420, lineHeight: 1.5 }}>{i18nT('components.formacion.ReproductorFormacion.la_leccion_escrita_paso_a', 'La lección escrita, paso a paso, está aquí debajo.')}</span>
          </div>
        ) : (
          <button type="button" onClick={() => empezar()} disabled={estado === 'cargando'} aria-label={i18nT('components.formacion.ReproductorFormacion.ver_el_video', 'Ver el vídeo')}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', padding: 0, cursor: 'pointer', background: cartel ? `center / cover no-repeat url(${cartel})` : '#0f172a' }}>
            <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.18)' }} />
            <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: compacto ? 58 : 88, height: compacto ? 58 : 88, borderRadius: '50%',
              background: 'rgba(255,255,255,.95)', color: C_BOTON, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 40px rgba(0,0,0,.4)' }}>
              {estado === 'cargando'
                ? <span style={{ fontSize: 14, fontWeight: 800 }}>…</span>
                : <svg width={compacto ? 22 : 34} height={compacto ? 22 : 34} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="8 5 19 12 8 19 8 5" /></svg>}
            </span>
            {posicionInicial > 5 && !compacto && (
              <span style={{ position: 'absolute', left: '50%', top: 'calc(50% + 62px)', transform: 'translateX(-50%)', background: 'rgba(0,0,0,.62)', color: '#fff', borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {tx('components.formacion.ReproductorFormacion.seguir_en_el', 'Seguir en el {{mmss}}', { mmss: mmss(posicionInicial) })}
              </span>
            )}
          </button>
        )}

        {/* LA MARCA DE AGUA: «Formación Biango» y nada más, quieta, sin recibir clics. */}
        <div data-marca-agua="" aria-hidden="true"
          style={{ position: 'absolute', top: compacto ? 8 : 14, right: compacto ? 10 : 16, pointerEvents: 'none', color: '#fff', opacity: 0.5, fontSize: compacto ? 10.5 : 13, fontWeight: 700,
            letterSpacing: '.02em', textShadow: '0 1px 3px rgba(0,0,0,.8)', zIndex: 3 }}>
          {tDato(MARCA)}
        </div>
        {/* B-2283 · LA FORMACIÓN POR INVITACIÓN: encima, discreto, de quién es la cuenta que mira (la del equipo no lleva nada). */}
        {sello && (
          <div data-sello-formacion="" aria-hidden="true"
            style={{ position: 'absolute', left: compacto ? 10 : 16, bottom: compacto ? 44 : 62, pointerEvents: 'none', userSelect: 'none', color: '#fff', opacity: 0.42, fontSize: compacto ? 10.5 : 12.5, fontWeight: 700,
              letterSpacing: '.02em', textShadow: '0 1px 3px rgba(0,0,0,.8)', zIndex: 3, maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sello}
          </div>
        )}

        {/* Los subtítulos, pintados por el reproductor (encima del vídeo y por encima de los controles). */}
        {cue && empezado && !celebrar && (
          <span style={{ position: 'absolute', left: '50%', bottom: aBorde((compacto || plano ? (visibles ? 52 : 14) : (visibles ? 96 : 22)) + 'px'), transform: 'translateX(-50%)', maxWidth: '82%', textAlign: 'center', background: 'rgba(0,0,0,.72)', color: '#fff',
            fontSize: compacto || plano ? 13 : 'clamp(13px, 1.25vw / var(--z, 1), 18px)', lineHeight: 1.4, fontWeight: 500, borderRadius: 8, padding: compacto ? '3px 8px' : '6px 14px', zIndex: 4, transition: 'bottom .25s ease', pointerEvents: 'none' }}>
            {cue.texto}
          </span>
        )}

        {/* LOS CONTROLES. En el ordenador, la barra arriba y los botones debajo; en el móvil y en la ayuda, todo en una fila. */}
        {mostrarControles && !(estado === 'error' && fallo) && !sinVideo && (() => {
          const fila = compacto || plano
          const barra = (
            <div ref={barraRef} role="slider" tabIndex={0} aria-label={i18nT('components.formacion.ReproductorFormacion.ir_a_un_momento_del', 'Ir a un momento del vídeo')} aria-valuemin={0} aria-valuemax={Math.round(total)} aria-valuenow={Math.round(t)} aria-valuetext={i18nT('components.formacion.ReproductorFormacion.de', '{{mmss}} de {{mmss2}}', { mmss: mmss(t), mmss2: mmss(total) })}
              onPointerDown={arrastrar} style={{ position: 'relative', height: fila ? 40 : 16, flex: fila ? 1 : undefined, minWidth: fila ? 60 : undefined, cursor: 'pointer', touchAction: 'none' }}>
              <span style={{ position: 'absolute', left: 0, right: 0, top: fila ? 18 : 6, height: 4, borderRadius: 4, background: 'rgba(255,255,255,.28)' }} />
              <span style={{ position: 'absolute', left: 0, top: fila ? 18 : 6, height: 4, borderRadius: 4, width: pct + '%', background: C_ACENTO }} />
              {total > 0 && capitulos.slice(1).map((c, i) => (
                <span key={i} title={c.titulo} style={{ position: 'absolute', top: fila ? 17 : 5, width: 3, height: 6, borderRadius: 2, background: 'rgba(255,255,255,.75)', left: ((100 * c.seg) / total).toFixed(2) + '%' }} />
              ))}
              <span style={{ position: 'absolute', top: fila ? 14 : 2, width: 12, height: 12, marginLeft: -6, borderRadius: '50%', background: '#fff', boxShadow: '0 0 0 3px color-mix(in srgb, ' + C_MARCA + ' 45%, transparent)', left: pct + '%' }} />
            </div>
          )
          const boton = { border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }
          return (
            <div className="fm-controles" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 5,
              padding: alBorde ? (fila ? '16px calc(6px + var(--sa-der)) calc(2px + var(--sa-abajo)) calc(6px + var(--sa-izq))' : '26px calc(16px + var(--sa-der)) calc(12px + var(--sa-abajo)) calc(16px + var(--sa-izq))') : (fila ? '16px 6px 2px' : '26px 16px 12px'),
              background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,.78))', display: 'flex', flexDirection: 'column', gap: 9, color: '#fff',
              opacity: visibles || !empezado ? 1 : 0, pointerEvents: visibles || !empezado ? 'auto' : 'none' }}>
              {!fila && barra}
              <div style={{ display: 'flex', alignItems: 'center', gap: fila ? 2 : 10, minWidth: 0 }}>
                <button type="button" onClick={alternar} aria-label={sonando ? i18nT('components.formacion.ReproductorFormacion.pausa', 'Pausa') : i18nT('components.formacion.ReproductorFormacion.reproducir', 'Reproducir')}
                  style={{ ...boton, width: 40, height: 40, borderRadius: 10, background: fila ? 'transparent' : 'rgba(255,255,255,.12)' }}>
                  {sonando
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="7 4 20 12 7 20 7 4" /></svg>}
                </button>
                <span style={{ fontSize: fila ? 12 : 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', marginRight: fila ? 6 : 0 }}>{mmss(t)} / {mmss(total)}</span>
                {fila && barra}
                {capituloActual && !fila && <span className="fm-solo-ancho" style={{ fontSize: 13, color: 'rgba(255,255,255,.78)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>· {capituloActual}</span>}
                {!fila && <span style={{ flex: 1 }} />}
                <button type="button" onClick={cambiarVelocidad} aria-label={i18nT('components.formacion.ReproductorFormacion.cambiar_la_velocidad', 'Cambiar la velocidad')}
                  style={{ ...boton, minWidth: fila ? 42 : 52, height: fila ? 40 : 34, border: fila ? 'none' : '1px solid rgba(255,255,255,.3)', borderRadius: 9, fontSize: fila ? 12 : 13, fontWeight: 800 }}>{velTexto(VELOCIDADES[vel])}</button>
                {cues.length > 0 && (
                  <button type="button" onClick={() => setCc(x => !x)} aria-pressed={cc} aria-label={i18nT('components.formacion.ReproductorFormacion.subtitulos', 'Subtítulos')}
                    style={{ ...boton, minWidth: fila ? 38 : undefined, height: fila ? 40 : 34, border: fila ? 'none' : '1px solid ' + (cc ? 'rgba(255,255,255,.6)' : 'rgba(255,255,255,.3)'), borderRadius: 9, background: !fila && cc ? 'rgba(255,255,255,.22)' : 'transparent', color: cc ? '#fff' : 'rgba(255,255,255,.55)', fontSize: 12.5, fontWeight: 800, padding: fila ? 0 : '0 10px' }}>{i18nT('components.formacion.ReproductorFormacion.cc', 'CC')}</button>
                )}
                <button type="button" onClick={pantallaCompleta} aria-label={completa || enPantallaCompleta ? i18nT('components.formacion.ReproductorFormacion.salir_de_pantalla_completa', 'Salir de pantalla completa') : i18nT('components.formacion.ReproductorFormacion.pantalla_completa', 'Pantalla completa')}
                  style={{ ...boton, width: fila ? 38 : 34, height: fila ? 40 : 34, borderRadius: 9 }}>
                  <Trazo d={completa || enPantallaCompleta ? ICONOS.salirCompleta : ICONOS.completa} s={18} />
                </button>
              </div>
            </div>
          )
        })()}

        {/* AL ACABAR: una celebración discreta y la siguiente lección. */}
        {celebrar && celebracion && (
          <div className="fm-velo" data-celebracion="" style={{ position: 'absolute', inset: 0, zIndex: 6, background: 'rgba(6,10,18,.74)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, maxWidth: 460, color: '#fff' }}>
              <span aria-hidden="true" style={{ position: 'relative', width: 84, height: 84 }}>
                {[['1', C_ACENTO], ['2', '#FBBF24'], ['3', '#FFFFFF'], ['4', C_ACENTO], ['5', '#34D399'], ['6', '#FFFFFF'], ['7', '#FBBF24'], ['8', '#34D399']].map(([n, c]) => (
                  <span key={n} className={'fm-ch fm-ch' + n} style={{ background: c }} />
                ))}
                <span className="fm-pop" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 16px 40px rgba(22,163,74,.45)' }}>
                  <Trazo d={ICONOS.check} s={40} w={3} />
                </span>
              </span>
              <div className="fm-m" style={{ marginTop: 6, fontWeight: 800, fontSize: 28 }}>{celebracion.titulo || i18nT('components.formacion.ReproductorFormacion.leccion_vista', 'Lección vista')}</div>
              {celebracion.texto && <div style={{ fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,.84)' }}>{celebracion.texto}</div>}
              <div style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                {celebracion.onSiguiente && (
                  <button type="button" onClick={celebracion.onSiguiente}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C_BOTON, color: C_SOBRE_BOTON, border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                    {i18nT('components.formacion.ReproductorFormacion.siguiente_leccion', 'Siguiente lección ')}<Trazo d={ICONOS.flecha} s={15} w={2.4} />
                  </button>
                )}
                <button type="button" onClick={() => { setCelebrar(false); const v = videoRef.current; if (v) { v.currentTime = 0; v.play().catch(() => {}) } }}
                  style={{ background: 'rgba(255,255,255,.12)', color: '#fff', border: '1px solid rgba(255,255,255,.28)', borderRadius: 12, padding: '12px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{i18nT('components.formacion.ReproductorFormacion.verla_otra_vez', 'Verla otra vez')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {compacto && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>{i18nT('components.formacion.ReproductorFormacion.biango_uso_exclusivo_de_clientes', '© Biango · Uso exclusivo de clientes. No se puede copiar, grabar ni compartir.')}</div>}
    </div>
  )
})

export default ReproductorFormacion
